import "./Federations.scss";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import FederationService from "../services/FederationService";
import { useLoader } from "../contexts/LoaderContext";
import SearchBar from "../components/SearchBar/SearchBar";
import TableComponent from "../components/TableComponent/TableComponent";
import StatsBoxContainer from "../components/StatsBoxContainer/StatsBoxContainer";
import { useLoading } from "../contexts/LoadingContext";
import { showToast } from "../components/Toaster";
import { SelectChangeEvent } from "@mui/material";
import { usePermissions, PERMISSIONS } from "../hooks/usePermissions";
import { IFederation } from "../interfaces/federation.interface";
import FederationModal from "../components/Federation/FederationModal";
import FederationRegistrationModal from "../components/Federation/FederationRegistrationModal";
import ConfirmDialog from "../components/ConfirmDialog/ConfirmDialog";

interface FederationStats {
  totalFederations: number;
}

function Federations() {
  const { showLoader, hideLoader } = useLoader();
  const loader = useLoading();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [error, setError] = useState<string | null>(null);
  const [federations, setFederations] = useState<IFederation[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [federationsLoaded, setFederationsLoaded] = useState(false);
  const initialLoadDone = useRef(false);
  const isLoading = useRef(false);
  const [federationStats, setFederationStats] = useState<FederationStats>({
    totalFederations: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [selectedFederation, setSelectedFederation] =
    useState<IFederation | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [federationToDelete, setFederationToDelete] =
    useState<IFederation | null>(null);

  const fetchFederations = useCallback(
    async (suppressLoader?: boolean, forceRefresh?: boolean) => {
      try {
        if (!initialLoadDone.current) {
          setFederationsLoaded(false);
        }

        if (!suppressLoader) {
          showLoader();
        } else {
          loader.setLoading(true);
        }

        setError(null);

        const response = await FederationService.getFederations();

        if (!response.error) {
          const federations: IFederation[] = response.data;
          setFederations(federations);

          // Calculate stats
          const stats: FederationStats = {
            totalFederations: federations?.length ?? 0,
          };
          setFederationStats(stats);
        } else {
          console.error("Error in fetching federations:", response.message);
          setError(response.message || "Error fetching federations");
          // Ensure stats remain valid even on error
          setFederationStats({
            totalFederations: 0,
          });
        }
      } catch (err: any) {
        console.error("Error fetching data:", err);
        setError(err.message || "An error occurred while fetching data");
        // Ensure stats remain valid even on error
        setFederationStats({
          totalFederations: 0,
        });
      } finally {
        setFederationsLoaded(true);
        initialLoadDone.current = true;

        setTimeout(() => {
          if (!suppressLoader) {
            hideLoader();
          } else {
            loader.setLoading(false);
          }
        }, 1000);
      }
    },
    [showLoader, hideLoader, loader]
  );

  // Memoize table data transformation
  const tableData = useMemo(() => {
    return federations.map((federation) => ({
      id: federation.id,
      name: federation.name,
      identifierId: federation.identifierId,
      contact: federation.phoneNumber || federation.contact || "N/A",
      email: federation.email || "N/A",
      address: federation.address || "N/A",
      clientCount: federation.clientList?.length || 0,
      userCount: federation.users?.length || 0,
      createdBy: federation.createdBy?.name || "N/A",
      createDate: federation.createDate
        ? new Date(federation.createDate).toLocaleDateString()
        : "N/A",
    }));
  }, [federations]);

  // Memoize filtered and paginated data
  const filteredAndPaginatedData = useMemo(() => {
    let filteredData = tableData;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredData = filteredData.filter((row) =>
        Object.values(row).some((value) =>
          value?.toString().toLowerCase().includes(query)
        )
      );
    }

    // Calculate pagination
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
      data: filteredData.slice(startIndex, endIndex),
      totalElements: filteredData.length,
      totalPages: Math.ceil(filteredData.length / pageSize),
    };
  }, [tableData, searchQuery, currentPage, pageSize]);

  // Memoize search suggestions
  const searchSuggestionsMemo = useMemo(() => {
    if (tableData.length > 0) {
      const suggestions = new Set<string>();

      tableData.forEach((row) => {
        if (row.name) suggestions.add(row.name);
        if (row.identifierId) suggestions.add(row.identifierId);
        if (row.contact && row.contact !== "N/A") suggestions.add(row.contact);
        if (row.email && row.email !== "N/A") suggestions.add(row.email);
        if (row.address && row.address !== "N/A") suggestions.add(row.address);
        if (row.createdBy && row.createdBy !== "N/A")
          suggestions.add(row.createdBy);
      });

      return Array.from(suggestions);
    }
    return [];
  }, [tableData]);

  // Memoize columns definition
  const columns = useMemo(
    () => [
      {
        header: "Federation Name",
        accessor: "name",
        sortable: true,
      },

      {
        header: "Contact Number",
        accessor: "contact",
        sortable: true,
      },
      {
        header: "Email",
        accessor: "email",
        sortable: true,
      },
      // {
      //   header: "Clients",
      //   accessor: "clientCount",
      //   sortable: true,
      // },
      // {
      //   header: "Users",
      //   accessor: "userCount",
      //   sortable: true,
      // },
      {
        header: "Identifier ID",
        accessor: "identifierId",
        sortable: true,
      },
      {
        header: "Created By",
        accessor: "createdBy",
        sortable: true,
      },
      {
        header: "Created Date",
        accessor: "createDate",
        sortable: true,
      },
    ],
    []
  );

  // Handle edit federation
  const handleEditFederation = useCallback((federation: any) => {
    setSelectedFederation(federation);
    setIsModalOpen(true);
  }, []);

  // Handle delete federation
  const handleDeleteFederation = useCallback(
    async (federationId: number) => {
      const federation = federations.find((f) => f.id === federationId);
      if (federation) {
        setFederationToDelete(federation);
        setDeleteDialogOpen(true);
      }
    },
    [federations]
  );

  // Memoize actions for the table
  const tableActions = useMemo(
    () => [
      {
        label: "Edit",
        callback: (row: Record<string, any>) => {
          const federation = federations.find((f) => f.id === row.id);
          if (federation) {
            handleEditFederation(federation);
          }
        },
        // isEnabled: () => can(PERMISSIONS.CAN_EDIT_FEDERATION),
        isEnabled: () => true,
      },
      {
        label: "Delete",
        callback: (row: Record<string, any>) => {
          handleDeleteFederation(row.id);
        },
        // isEnabled: () => can(PERMISSIONS.CAN_DELETE_FEDERATION),
        isEnabled: () => true,
      },
    ],
    [federations, can, handleEditFederation, handleDeleteFederation]
  );

  // Memoize stats data
  const statsData = useMemo(
    () => [
      {
        name: "Total Federations",
        value: federationStats.totalFederations ?? 0,
        valueColor: "#59585B",
      },
    ],
    [federationStats]
  );

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchFederations(false);
      } finally {
        // No need to hide loader here as it's handled in fetchFederations
      }
    };

    fetchData();
  }, [fetchFederations]);

  // Update search suggestions when table data changes
  useEffect(() => {
    setSearchSuggestions(searchSuggestionsMemo);
  }, [searchSuggestionsMemo]);

  // Handle manual refresh
  const handleManualRefresh = useCallback(() => {
    fetchFederations(true, true);
  }, [fetchFederations]);

  // Handle search input changes
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on search
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      setPageSize(Number(event.target.value));
      setCurrentPage(1); // Reset to first page when changing page size
    },
    []
  );

  // Handle add federation
  const handleAddFederation = useCallback(() => {
    setIsRegistrationModalOpen(true);
  }, []);

  // Handle modal success
  const handleModalSuccess = useCallback(() => {
    fetchFederations(true, true);
  }, [fetchFederations]);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedFederation(null);
  }, []);

  // Handle registration modal close
  const handleRegistrationModalClose = useCallback(() => {
    setIsRegistrationModalOpen(false);
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!federationToDelete) return;

    try {
      showLoader();
      await FederationService.deleteFederation(federationToDelete.id);
      showToast("Federation deleted successfully", "success");
      // Refresh the list
      fetchFederations(true, true);
    } catch (error) {
      console.error("Error deleting federation:", error);
      showToast("Error deleting federation", "error");
    } finally {
      hideLoader();
      setDeleteDialogOpen(false);
      setFederationToDelete(null);
    }
  }, [federationToDelete, fetchFederations, showLoader, hideLoader]);

  // Handle delete dialog close
  const handleDeleteDialogClose = useCallback(() => {
    setDeleteDialogOpen(false);
    setFederationToDelete(null);
  }, []);

  return (
    <div className="federations-page p-[20px]">
      <StatsBoxContainer stats={statsData} loading={!federationsLoaded} />

      <div className="flex justify-between items-center py-[30px]">
        <SearchBar
          placeholder="Search federations..."
          onSearch={handleSearchChange}
          showClearButton={true}
          suggestions={searchSuggestions}
          onSuggestionClick={(suggestion) => {
            setSearchQuery(suggestion);
            setCurrentPage(1); // Reset to first page on suggestion click
          }}
        />
        <div className="flex items-center space-x-2">
          {can(PERMISSIONS.CAN_CREATE_FEDERATION) && (
            <button
              type="button"
              className="primary_button"
              onClick={handleAddFederation}
            >
              Add Federation
            </button>
          )}
          <div className="flex items-center space-x-2">
            <button
              type="button"
              className={`secondary_button ${
                loader.isLoading ? "opacity-70" : ""
              }`}
              onClick={handleManualRefresh}
              disabled={loader.isLoading}
            >
              <div className="flex items-center">
                {loader.isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Refreshing...
                  </>
                ) : (
                  "Refresh"
                )}
              </div>
            </button>
          </div>
        </div>
      </div>

      {federationsLoaded && !loader.isLoading && tableData.length === 0 ? (
        <div className="flex justify-center items-center">
          <p className="text-gray-500">No federations found</p>
        </div>
      ) : (
        <div className="table">
          <TableComponent
            showPagination={true}
            columns={columns}
            data={tableData}
            searchQuery={searchQuery}
            showCheckboxes={false}
            uniqueAccessor="id"
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={Math.ceil(tableData.length / pageSize)}
            totalElements={tableData.length}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            enableClientSideFiltering={true}
            actions={tableActions}
          />
        </div>
      )}

      {/* Federation Modal */}
      <FederationModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        federation={selectedFederation}
        onSuccess={handleModalSuccess}
      />

      {/* Federation Registration Modal */}
      <FederationRegistrationModal
        isOpen={isRegistrationModalOpen}
        onClose={handleRegistrationModalClose}
        onSuccess={handleModalSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Federation"
        message={`Are you sure you want to delete the federation "${federationToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </div>
  );
}

export default Federations;
